import React, { useState, useEffect } from 'react';
import HomePage from './pages/Home';
import List from './pages/List';
import SearchResult from './pages/Search';
import UserStats from './pages/Stats';
import UserAccount from './pages/Account';
import Login from './pages/Login';
import NavbarApp from './components/NavbarApp';
import TopBar from './components/TopBar';

import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './firebaseConfig';
import {
  collection,
  doc,
  getDocs,
  query,
  runTransaction,
  where,
} from 'firebase/firestore';
import { PersistentProvider } from './components/PersistentContext';
import { deleteLocalStorage, setMaxWidth } from './utils/functions';
import NotificationBanner from './components/NotificationBanner';

import { useTranslation } from './i18n';
import Loader from './components/Loader';
import NavbarLogo from './components/NavbarLogo';
import ScrollToTop from './components/ScrollToTop';

function App() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState('');
  const [userSet, setUserSet] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loggedIn, setLoggedIn] = useState(null);
  const [books, setBooks] = useState([]);
  const [notificationQueue, setNotificationQueue] = useState([]);
  const [currentNotification, setCurrentNotification] = useState(null);

  // Initialize theme from localStorage or default to light
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme : 'light'; // Default to 'light' if no theme is selected
  });

  useEffect(() => {
    const root = document.documentElement;

    const applyTheme = (theme) => {
      root.setAttribute('data-theme', theme);
      updateThemeColor(theme);
    };

    applyTheme(theme);

    // Save theme to localStorage
    localStorage.setItem('theme', theme);
  }, [theme]);

  const updateThemeColor = (theme) => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content',
        theme === 'dark' ? '#121314' : '#ffffff',
      );
    }
  };

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
  };
  const { changeLanguage } = useTranslation();

  useEffect(() => {
    if (notificationQueue.length > 0 && !currentNotification) {
      setCurrentNotification(notificationQueue[0]);
      setNotificationQueue((prevQueue) => prevQueue.slice(1));
    }
  }, [notificationQueue, currentNotification]);

  const showNotification = (message) => {
    setNotificationQueue((prevQueue) => [...prevQueue, { message }]);
  };

  const closeNotification = () => {
    setCurrentNotification(null);
  };

  const fetchBooks = async () => {
    if (!user?.uid) {
      console.error('User ID is undefined');
      return;
    }

    try {
      const q = query(
        collection(db, 'UserBooks'),
        where('userID', '==', user.uid),
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setBooks([]); // Set an empty list if no books found
        return;
      }

      const booksList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBooks(booksList);
    } catch (error) {
      console.error('Error fetching user books:', error);
    }
  };

  const fetchUserData = async (email, getBooks, caller = 'unknown caller') => {
    try {
      const q = query(collection(db, 'Users'), where('email', '==', email));
      const querySnapshot = await getDocs(q);
      const fetchedUserData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (fetchedUserData.length > 0) {
        setUserData(fetchedUserData[0]);
        return fetchedUserData[0]; // Return the first (and likely only) user's data
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error fetching user data: ', error, caller);
      return null;
    } finally {
      if (getBooks) {
        fetchBooks();
      }
    }
  };

  const updateCurrentlyReading = async (book, user, updateCurrentBook) => {
    if (!book?.userID) {
      console.error('Book User ID is undefined');
      return;
    }

    const userRef = doc(db, 'Users', book.userID);

    try {
      await runTransaction(db, async (transaction) => {
        transaction.update(userRef, {
          currentlyReading: updateCurrentBook
            ? {
                bookID: book.bookID,
                title: book.title,
                author_name: book.author_name,
                isbn: book.isbn,
                number_of_pages: book.number_of_pages || 0,
                publish_date: book.publish_date || '',
                publisher: book.publisher || '',
                cover_url: book.cover_url || [false],
                pages: book.pages || 0,
                time: book.time || 0,
              }
            : {},
        });
      });
      fetchUserData(user.email, false, 'updateCurrentlyReading, App.js');
    } catch (error) {
      console.error('Error updating Users currentlyReading: ', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is signed in
        setUser(user);
        setUserSet(true); // Indicate that the user state has been set
        setLoggedIn(true);
      } else {
        // User is signed out
        setUser(null);
        setLoggedIn(false);
        deleteLocalStorage();
      }
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (user && userSet) {
      fetchUserData(user.email, true, 'useEffect, App.js');
      setUserSet(false); // Reset userSet state
    }
  }, [user, userSet]);

  useEffect(() => {
    setMaxWidth();
  }, []);

  const handleLoadingComplete = () => {
    setLoading(false);
  };

  return [
    loading && (
      <Loader
        onFinish={handleLoadingComplete}
        key="loader"
        loggedIn={loggedIn}
      />
    ),
    <PersistentProvider key="persistant">
      <Router basename="/">
        <ScrollToTop />
        <Routes>
          <Route
            path="/"
            element={
              <HomePage
                user={user}
                loggedIn={loggedIn}
                userData={userData}
                books={books}
                fetchBooks={fetchBooks}
                fetchUserData={fetchUserData}
                showNotification={showNotification}
                updateCurrentlyReading={updateCurrentlyReading}
                changeLanguage={changeLanguage}
                theme={theme}
                handleThemeChange={handleThemeChange}
              />
            }
          />
          <Route
            path="/index.html"
            element={
              <HomePage
                user={user}
                loggedIn={loggedIn}
                userData={userData}
                books={books}
                fetchBooks={fetchBooks}
                fetchUserData={fetchUserData}
                showNotification={showNotification}
                updateCurrentlyReading={updateCurrentlyReading}
                changeLanguage={changeLanguage}
                theme={theme}
                handleThemeChange={handleThemeChange}
              />
            }
          />
          <Route
            path="/books"
            element={
              <List
                user={user}
                userData={userData}
                fetchBooks={fetchBooks}
                fetchUserData={fetchUserData}
                books={books}
                showNotification={showNotification}
                updateCurrentlyReading={updateCurrentlyReading}
              />
            }
          />
          <Route
            path="/search"
            element={
              <SearchResult
                user={user}
                fetchBooks={fetchBooks}
                fetchUserData={fetchUserData}
                showNotification={showNotification}
                updateCurrentlyReading={updateCurrentlyReading}
              />
            }
          />
          <Route
            path="/stats"
            element={
              <UserStats
                user={user}
                books={books}
                userData={userData}
                theme={theme}
              />
            }
          />
          <Route
            path="/account"
            element={
              <UserAccount
                user={user}
                setUser={setUser}
                userData={userData}
                books={books}
                fetchBooks={fetchBooks}
                showNotification={showNotification}
                fetchUserData={fetchUserData}
                theme={theme}
                handleThemeChange={handleThemeChange}
                updateCurrentlyReading={updateCurrentlyReading}
              />
            }
          />
          <Route
            path="/login"
            element={
              <Login
                user={user}
                userData={userData}
                books={books}
                fetchBooks={fetchBooks}
                fetchUserData={fetchUserData}
                showNotification={showNotification}
                updateCurrentlyReading={updateCurrentlyReading}
                changeLanguage={changeLanguage}
                theme={theme}
                handleThemeChange={handleThemeChange}
              />
            }
          />
        </Routes>
        {user && (
          <div className="navigation">
            {
              <TopBar
                user={userData}
                fetchBooks={fetchBooks}
                sessions={userData?.sessions || []}
                fetchUserData={fetchUserData}
                showNotification={showNotification}
              />
            }
            {<NavbarApp />}
            {<NavbarLogo />}
          </div>
        )}
      </Router>
      {currentNotification && (
        <NotificationBanner
          message={currentNotification.message}
          onClose={closeNotification}
        />
      )}
    </PersistentProvider>,
  ];
}

export default App;
